import { Link } from "react-router-dom";
import { FileText, Receipt, UserRound, HardHat, Plus, ChevronRight, Sparkles, ArrowRight } from "lucide-react";
import { eur } from "@/lib/studioApi";

/* Icône « app moderne » : pastille teintée + pictogramme saturé */
export const TONES = {
  amber: { bg: "bg-amber-50", fg: "text-amber-600", ring: "ring-amber-200" },
  blue: { bg: "bg-[#EEF1FC]", fg: "text-[#3A5CC8]", ring: "ring-[#C9D4F4]" },
  navy: { bg: "bg-[#EEF1FC]", fg: "text-[#0F2645]", ring: "ring-[#C9D4F4]" },
  orange: { bg: "bg-orange-50", fg: "text-[#F97316]", ring: "ring-orange-200" },
  emerald: { bg: "bg-emerald-50", fg: "text-emerald-600", ring: "ring-emerald-200" },
  rose: { bg: "bg-rose-50", fg: "text-rose-600", ring: "ring-rose-200" },
  slate: { bg: "bg-[#F3F5FC]", fg: "text-[#5C6A87]", ring: "ring-[#E2E7F7]" },
};

export const AppIcon = ({ icon: Icon, tone = "blue", size = "md", className = "" }) => {
  const t = TONES[tone] || TONES.blue;
  const dims = size === "lg" ? "h-14 w-14 rounded-2xl" : size === "sm" ? "h-8 w-8 rounded-lg" : "h-10 w-10 rounded-xl";
  const ic = size === "lg" ? "h-6 w-6" : size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <span className={`inline-flex shrink-0 items-center justify-center ${dims} ${t.bg} ${className}`}>
      <Icon className={`${ic} ${t.fg}`} strokeWidth={2.2} />
    </span>
  );
};

const QUICK = [
  ["Devis", "/studio/devis?nouveau=1", FileText, "amber", "quick-quote"],
  ["Facture", "/studio/factures?nouveau=1", Receipt, "blue", "quick-invoice"],
  ["Client", "/studio/clients?nouveau=1", UserRound, "navy", "quick-client"],
  ["Chantier", "/studio/dossiers?nouveau=1", HardHat, "orange", "quick-dossier"],
];

/* Barre d'actions rapides compacte (une ligne, boutons nets) */
export const QuickActions = ({ name }) => (
  <>
  <section data-testid="cockpit-quick" className="dv-card-in mt-5 flex flex-wrap items-center justify-between gap-3">
    <p className="font-heading text-base font-bold text-[#0F2645]">Bienvenue{name ? ` ${name}` : ""} <HardHat className="ml-1 inline h-4 w-4 text-[#F97316]" /></p>
    <div className="flex flex-wrap gap-2">
      {QUICK.map(([label, to, Icon, tone, tid]) => {
        const t = TONES[tone];
        return (
          <Link key={label} to={to} data-testid={tid} className="group inline-flex h-10 items-center gap-2 rounded-xl border border-[#E2E7F7] bg-white pl-2 pr-3.5 text-[13px] font-semibold text-[#0F2645] transition-colors duration-150 hover:border-[#C9D4F4] hover:bg-[#F6F8FD] active:scale-[0.98]">
            <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${t.bg}`}><Icon className={`h-3.5 w-3.5 ${t.fg}`} strokeWidth={2.4} /></span>
            {label}
            <Plus className="h-3.5 w-3.5 text-[#93A0BC] transition-colors group-hover:text-[#3A5CC8]" strokeWidth={2.5} />
          </Link>
        );
      })}
    </div>
  </section>
  <Link to="/studio/assistant" data-testid="cockpit-assistant-cta" className="dv-card-in group mt-4 flex items-center gap-4 overflow-hidden rounded-2xl bg-[#0F2645] p-4 text-white shadow-[0_18px_40px_-24px_rgba(8,16,77,0.7)] transition-transform duration-150 hover:-translate-y-0.5 sm:p-5">
    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#3A5CC8] shadow-[0_8px_20px_-8px_rgba(58,92,200,0.9)]"><Sparkles className="h-6 w-6" /></span>
    <span className="min-w-0 flex-1">
      <span className="block font-heading text-base font-bold leading-tight sm:text-lg">Un devis en 5 minutes, pas en 3 heures ce soir</span>
      <span className="mt-1 block text-sm leading-snug text-[#C8D2EC]">Dictez le chantier ou envoyez la photo de la plaque : l'assistant pose 2-3 questions, suggère la suite et prépare le devis avec vos prix.</span>
    </span>
    <span className="hidden shrink-0 items-center gap-1 rounded-xl bg-white/10 px-3.5 py-2 text-sm font-semibold transition-colors group-hover:bg-white/20 sm:inline-flex">Commencer <ArrowRight className="h-4 w-4" /></span>
    <ArrowRight className="h-5 w-5 shrink-0 text-[#9FB4F2] sm:hidden" />
  </Link>
  </>
);

const Bar = ({ parts }) => {
  const total = parts.reduce((s, p) => s + (p.value || 0), 0) || 1;
  return (
    <div className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-[#EEF1FC]">
      {parts.filter((p) => p.value > 0).map((p) => <span key={p.label} className={p.cls} style={{ width: `${(p.value / total) * 100}%` }} />)}
    </div>
  );
};

/* Bloc « Factures en attente » : barre en cours / en retard */
export const InvoicesWidget = ({ inv }) => {
  if (!inv) return null;
  const late = inv.late_amount || 0;
  const onTime = Math.max(0, (inv.open_amount || 0) - late);
  return (
    <Link to="/studio/factures" data-testid="cockpit-invoices-widget" className="dv-card-in block rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] transition-colors hover:border-[#3A5CC8]/40">
      <div className="flex items-center justify-between">
        <p className="font-heading text-base font-bold text-[#0F2645]">Factures en attente ({inv.open_count || 0})</p>
        <ChevronRight className="h-5 w-5 text-[#3A5CC8]" />
      </div>
      <Bar parts={[{ label: "ok", value: onTime, cls: "bg-orange-400" }, { label: "late", value: late, cls: "bg-rose-500" }]} />
      <div className="mt-2 flex items-center justify-between text-sm">
        <span className="font-mono font-semibold tabular-nums text-[#0F2645]">{eur(inv.open_amount)}</span>
        <span className="font-mono tabular-nums text-rose-600">{eur(late)} en retard</span>
      </div>
    </Link>
  );
};

const ROWS = [
  ["accepted", "acceptés", "bg-emerald-500", "text-emerald-500"],
  ["pending", "en attente", "bg-amber-400", "text-amber-400"],
  ["refused", "refusés", "bg-rose-500", "text-rose-500"],
  ["cancelled", "annulés", "bg-slate-600", "text-slate-600"],
];

/* Bloc « Devis » : barre acceptés / attente / refusés / annulés + légende */
export const QuotesWidget = ({ bd, toInvoice }) => {
  if (!bd) return null;
  const total = ROWS.reduce((s, [k]) => s + (bd[k]?.amount || 0), 0);
  return (
    <Link to="/studio/devis" data-testid="cockpit-quotes-widget" className="dv-card-in block rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] transition-colors hover:border-[#3A5CC8]/40">
      <div className="flex items-center justify-between">
        <p className="font-heading text-base font-bold text-[#0F2645]">Devis</p>
        <ChevronRight className="h-5 w-5 text-[#3A5CC8]" />
      </div>
      {toInvoice > 0 ? (
        <p className="mt-1 text-sm text-[#5C6A87]"><span className="font-semibold text-[#3A5CC8]">{toInvoice} devis</span> signé{toInvoice > 1 ? "s" : ""} à facturer</p>
      ) : null}
      <Bar parts={ROWS.map(([k, label, cls]) => ({ label, value: bd[k]?.amount || 0, cls }))} />
      <ul className="mt-3 divide-y divide-[#EEF1FC]">
        {ROWS.map(([k, label, cls]) => (
          <li key={k} className="flex items-center justify-between py-2 text-sm">
            <span className="inline-flex items-center gap-2 text-[#0F2645]"><span className={`h-3 w-3 rounded-[4px] ${cls}`} /> {bd[k]?.count || 0} {label}</span>
            <span className="font-mono tabular-nums text-[#5C6A87]">{eur(bd[k]?.amount)}</span>
          </li>
        ))}
        <li className="flex items-center justify-between pt-2 text-sm font-bold text-[#0F2645]"><span>Total</span><span className="font-mono tabular-nums">{eur(total)}</span></li>
      </ul>
    </Link>
  );
};

export const AiButton = ({ to, testId }) => (
  <Link to={to} data-testid={testId} className="inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-[#F97316]/30 bg-orange-50 px-4 text-sm font-semibold text-[#C2410C] transition-colors hover:bg-orange-100">
    <Sparkles className="h-4 w-4" /> IA
  </Link>
);
